import * as React from 'react';
import { Suspense, lazy, useEffect, ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
// Import devtools only in development
const ReactQueryDevtools = process.env.NODE_ENV === 'production'
  ? () => null // Return null component in production
  : React.lazy(() => import('@tanstack/react-query-devtools').then(module => ({ default: module.ReactQueryDevtools })));
import { BrowserRouter, Routes, Route, Outlet, Navigate } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import { AuthProvider } from "@/context/AuthContext";
import { GroupProvider } from "@/lib/context/GroupContext";
import { PushNotificationInitializer } from "@/components/notifications/PushNotificationInitializer";
import { UIProvider } from '@/context/UIContext';
import { OfflineProvider } from '@/context/OfflineContext';
import { UserPreferencesProvider } from '@/context/UserPreferencesContext';
import { ThemeProvider, setupSystemThemeListener } from '@/context/ThemeContext';
import { SharedLinkProvider } from '@/context/SharedLinkContext';
import { LanguageProvider } from '@/context/LanguageContext';
import { DemoProvider } from '@/context/DemoContext';
import { OnboardingProvider } from "@/context/OnboardingContext";
import { useUI } from '@/context/UIContext';
import FeedbackNotificationInitializer from '@/components/feedback/FeedbackNotificationInitializer';
import { AppInstallPrompt } from '@/components/ui/app-install-prompt';

import { PageLoader } from "@/components/ui/page-loader";
import V2Layout from "@/components/layout/V2Layout";
import HomePageV2 from "@/pages/v2/HomePage";
import MatchesPageV2 from "@/pages/v2/MatchesPage";
import SquadPageV2 from "@/pages/v2/SquadPage";
import StatsPageV2 from "@/pages/v2/StatsPage";
import ProfilePage from "@/pages/ProfilePage";

// Lazy-loaded components
const UnifiedLayout = lazy(() => import("@/components/layout/UnifiedLayout"));
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const SignUpPage = lazy(() => import("@/pages/SignUpPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const PlayersPage = lazy(() => import("@/pages/PlayersPage"));
const MatchesPage = lazy(() => import("@/pages/MatchesPage"));
const LeaderboardPage = lazy(() => import("@/pages/LeaderboardPage"));
const TeamGeneratorPage = lazy(() => import("@/pages/TeamGeneratorPage"));
const ChemistryPage = lazy(() => import("@/pages/ChemistryPage"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const GroupSelectionPage = lazy(() => import("@/pages/GroupSelectionPage"));
// Name-based import is now integrated into the standard import

// Shared (view-only) pages
const SharedEntryPage = lazy(() => import("@/pages/shared/SharedEntryPage"));
const SharedDashboardPage = lazy(() => import("@/pages/shared/SharedDashboardPage"));

// Demo pages
const DemoPlayersPage = lazy(() => import("@/pages/demo/DemoPlayersPage"));
const DemoMatchesPage = lazy(() => import("@/pages/demo/DemoMatchesPage"));
const SharedPlayersPage = lazy(() => import("@/pages/shared/SharedPlayersPage"));
const SharedMatchesPage = lazy(() => import("@/pages/shared/SharedMatchesPage"));
const SharedLeaderboardPage = lazy(() => import("@/pages/shared/SharedLeaderboardPage"));
const SharedTeamGeneratorPage = lazy(() => import("@/pages/shared/SharedTeamGeneratorPage"));
const SharedChemistryPage = lazy(() => import("@/pages/shared/SharedChemistryPage"));

// Sharing components
const ShareLinkViewer = lazy(() => import("@/components/sharing/ShareLinkViewer"));
const RsvpPage = lazy(() => import("@/pages/RsvpPage"));
const PublicRsvpPage = lazy(() => import("@/pages/PublicRsvpPage"));
const PublicPollPage = lazy(() => import("@/pages/PublicPollPage"));
import PublicJoinPage from "@/pages/PublicJoinPage";
import PublicHomePage from "@/pages/v2/PublicHomePage";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      gcTime: 24 * 60 * 60 * 1000, // 24 hours (renamed from cacheTime in v5)
    },
  },
});

// Ensure the queryClient is created only once
const getQueryClient = (() => {
  return queryClient;
})();

const SuspenseFallback = () => <PageLoader className="min-h-screen" />;

const AppProviders = ({ children }: { children: ReactNode }) => (
  <QueryClientProvider client={getQueryClient}>
    <BrowserRouter>
      <TooltipProvider>
        <AuthProvider>
          <GroupProvider>
            <OfflineProvider>
              <UserPreferencesProvider>
                <ThemeProvider>
                  <LanguageProvider>
                    <SharedLinkProvider>
                      <UIProvider>
                        {children}
                      </UIProvider>
                    </SharedLinkProvider>
                  </LanguageProvider>
                </ThemeProvider>
              </UserPreferencesProvider>
            </OfflineProvider>
          </GroupProvider>
        </AuthProvider>
      </TooltipProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

const ProtectedPagesLayout = () => (
  <ProtectedRoute>
    <PushNotificationInitializer />
    <UnifiedLayout>
      <Outlet />
    </UnifiedLayout>
  </ProtectedRoute>
);

const LegacyDashboardRoute = () => {
  const { uiMode, showUIToggle } = useUI();

  if (uiMode !== 'current' || !showUIToggle) {
    return <Navigate to="/v2/home" replace />;
  }

  return (
    <UnifiedLayout showExperimentalBadge>
      <DashboardPage />
    </UnifiedLayout>
  );
};

const App = () => {
  // Initialize system theme listener
  useEffect(() => {
    const cleanup = setupSystemThemeListener();
    return cleanup;
  }, []);

  return (
    <QueryClientProvider client={getQueryClient}>
      <BrowserRouter>
        <TooltipProvider>
          <AuthProvider>
            <OnboardingProvider>
            <GroupProvider>
              <OfflineProvider>
                <UserPreferencesProvider>
                  <ThemeProvider>
                    <LanguageProvider>
                      <SharedLinkProvider>
                        <UIProvider>
                          <Suspense fallback={<SuspenseFallback />}>
                          <Routes>
                          <Route path="/" element={<LandingPage />} />
                          <Route path="/login" element={<LoginPage />} />
                          <Route path="/signup" element={<SignUpPage />} />
                          <Route path="/reset-password" element={<ResetPasswordPage />} />
                          <Route path="/group-selection" element={
                            <ProtectedRoute>
                              <GroupSelectionPage />
                            </ProtectedRoute>
                          } />
                          {/* Name-based import is now integrated into the standard import */}

                          {/* Protected Routes */}
                          <Route path="/dashboard" element={
                            <ProtectedRoute>
                              <Navigate to="/v2/home" replace />
                            </ProtectedRoute>
                          } />
                          <Route path="/legacy/dashboard" element={
                            <ProtectedRoute>
                              <LegacyDashboardRoute />
                            </ProtectedRoute>
                          } />
                          <Route path="/v2" element={
                            <ProtectedRoute>
                              <V2Layout />
                            </ProtectedRoute>
                          }>
                            <Route index element={<Navigate to="/v2/home" replace />} />
                            <Route path="home" element={<HomePageV2 />} />
                            <Route path="play" element={<Navigate to="/v2/matches" replace />} />
                            <Route path="matches" element={<MatchesPageV2 />} />
                            <Route path="squad" element={<SquadPageV2 />} />
                            <Route path="stats" element={<StatsPageV2 />} />
                            <Route path="profile" element={<ProfilePage />} />
                          </Route>
                          <Route path="/players" element={
                            <ProtectedRoute>
                              <UnifiedLayout>
                                <PlayersPage />
                              </UnifiedLayout>
                            </ProtectedRoute>
                          } />
                          <Route path="/matches" element={
                            <ProtectedRoute>
                              <UnifiedLayout>
                                <MatchesPage />
                              </UnifiedLayout>
                            </ProtectedRoute>
                          } />
                          {/* Leaderboard, Chemistry, and Team Generator now cut over to V2 tabs */}
                          <Route path="/leaderboard" element={<Navigate to="/v2/stats" replace />} />
                          <Route path="/chemistry" element={<Navigate to="/v2/stats" replace />} />
                          <Route path="/team-generator" element={<Navigate to="/v2/matches" replace />} />
                          <Route path="/profile" element={<Navigate to="/v2/profile" replace />} />
                          <Route path="/more" element={<Navigate to="/v2/profile" replace />} />
                          {/* Shared (View-Only) Routes */}
                          <Route path="/shared/:groupId" element={<SharedEntryPage />} />
                          <Route path="/shared/:groupId/dashboard" element={<SharedDashboardPage />} />
                          <Route path="/shared/:groupId/players" element={<SharedPlayersPage />} />
                          <Route path="/shared/:groupId/matches" element={<SharedMatchesPage />} />
                          <Route path="/shared/:groupId/team-generator" element={<SharedTeamGeneratorPage />} />
                          {/* Leaderboard and Chemistry are integrated into the Shared Dashboard */}

                          <Route path="/rsvp/:eventId" element={<RsvpPage />} />
                          <Route path="/rsvp/public/:token" element={<PublicRsvpPage />} />
                          <Route path="/poll/public/:token" element={<PublicPollPage />} />
                          <Route path="/join/public/:token" element={<PublicJoinPage />} />
                          <Route path="/join/public/:token/home" element={<PublicHomePage />} />

                          {/* Enhanced Sharing Routes */}
                          <Route path="/s/:linkId" element={<ShareLinkViewer />} />

                          {/* Demo Routes */}
                          <Route path="/demo" element={<Navigate to="/v2/home" replace />} />
                          <Route path="/demo/dashboard" element={<Navigate to="/v2/home" replace />} />
                          <Route path="/demo/players" element={
                            <DemoProvider>
                              <DemoPlayersPage />
                            </DemoProvider>
                          } />
                          <Route path="/demo/matches" element={
                            <DemoProvider>
                              <DemoMatchesPage />
                            </DemoProvider>
                          } />

                          <Route path="*" element={<NotFound />} />
                        </Routes>
                          </Suspense>
                          <Toaster />
                          <Analytics />
                          <FeedbackNotificationInitializer />
                          <AppInstallPrompt />
                        </UIProvider>
                      </SharedLinkProvider>
                    </LanguageProvider>
                  </ThemeProvider>
                </UserPreferencesProvider>
              </OfflineProvider>
            </GroupProvider>
            </OnboardingProvider>
          </AuthProvider>
        </TooltipProvider>
        {/* Only include devtools in development */}
        {process.env.NODE_ENV !== 'production' && (
          <Suspense fallback={null}>
            <ReactQueryDevtools initialIsOpen={false} />
          </Suspense>
        )}
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
